<section class="atmos-spirit-tree" [class.has-pad-bottom]="padBottom()" [class.is-highlighted]="highlight()" [attr.data-tree]="tree().guid">
  <div class="atmos-spirit-tree__body">
    @if (visibleName()) {
      <header class="atmos-spirit-tree__header">
        <h3 class="atmos-spirit-tree__title">
          @if (nameLink(); as link) {
            <a class="atmos-text-link" [routerLink]="link" [queryParams]="nameQueryParams()">{{ visibleName() }}</a>
          } @else {
            {{ visibleName() }}
          }
          @if (eventDate(); as d) {
            <span class="atmos-spirit-tree__date">(<app-date [date]="d"></app-date>)</span>
          }
          @if (nameTag(); as tag) {
            <span class="atmos-spirit-tree__tag">{{ tag }}</span>
          }
        </h3>
      </header>
    }

    @if (showControls()) {
      <nav class="atmos-spirit-tree__controls" aria-label="Spirit tree actions">
        <div class="atmos-spirit-tree__seg" role="group" aria-label="Node click action">
          <button type="button" class="atmos-spirit-tree__seg-btn"
                  [class.is-active]="currentAction() === 'unlock'"
                  [attr.aria-pressed]="currentAction() === 'unlock'"
                  (click)="setAction('unlock')"
                  aria-label="Unlock / lock items"
                  skyTooltip="Unlock / lock items (U)">
            <mat-icon>lock_open</mat-icon>
          </button>
          <button type="button" class="atmos-spirit-tree__seg-btn"
                  [class.is-active]="currentAction() === 'navigate'"
                  [attr.aria-pressed]="currentAction() === 'navigate'"
                  (click)="setAction('navigate')"
                  aria-label="Open item page"
                  skyTooltip="Open item page (N)">
            <mat-icon>search</mat-icon>
          </button>
          <button type="button" class="atmos-spirit-tree__seg-btn"
                  [class.is-active]="currentAction() === 'favourite'"
                  [attr.aria-pressed]="currentAction() === 'favourite'"
                  (click)="setAction('favourite')"
                  aria-label="Wishlist item"
                  skyTooltip="Wishlist item (F)">
            <mat-icon>star</mat-icon>
          </button>
        </div>

        <!-- Every label is rendered, stacked in one grid cell, so the box is as wide
             as the longest one and switching modes moves nothing. -->
        <span class="atmos-spirit-tree__seg-name" aria-hidden="true">
          <span [class.is-shown]="currentAction() === 'unlock'">Unlock</span>
          <span [class.is-shown]="currentAction() === 'navigate'">Navigate</span>
          <span [class.is-shown]="currentAction() === 'favourite'">Wishlist</span>
        </span>

        <span class="atmos-spirit-tree__controls-spacer"></span>

        <button type="button" class="atmos-btn atmos-btn--sm atmos-btn--icon"
                (click)="unlockAll()"
                [attr.aria-label]="hasLocked() ? 'Unlock all items' : 'Lock all items'" [skyTooltip]="hasLocked() ? 'Unlock all items' : 'Lock all items'">
          <mat-icon>enhanced_encryption</mat-icon>
        </button>
        <span class="atmos-spirit-tree__menu">
          <button type="button" class="atmos-btn atmos-btn--sm atmos-btn--icon"
                  [class.atmos-btn--active]="showMenu()"
                  (click)="toggleMenu()" skyTooltip="More actions">
            <mat-icon>menu</mat-icon>
          </button>
          @if (showMenu()) {
            <div class="atmos-spirit-tree__menu-panel" role="menu">
              <button type="button" class="atmos-btn atmos-btn--sm atmos-spirit-tree__menu-item"
                      skyTooltip="Copied!" #ttCopyImage="skyTooltip" manual placement="top" [closeDelay]="1000"
                      (click)="exportPng('clipboard', ttCopyImage)">
                <mat-icon>file_copy</mat-icon><span>Copy image</span>
              </button>
              <button type="button" class="atmos-btn atmos-btn--sm atmos-spirit-tree__menu-item"
                      (click)="exportPng('share')">
                <mat-icon>share</mat-icon><span>Share image</span>
              </button>
              <button type="button" class="atmos-btn atmos-btn--sm atmos-spirit-tree__menu-item"
                      (click)="editTree()">
                <mat-icon>edit</mat-icon><span>Edit tree</span>
              </button>
            </div>
          }
        </span>
      </nav>
    }

    @if (tree().node) {
      <!-- Columns-first layout for node trees: left / center / right columns,
           root at bottom (flex-direction: column-reverse), side columns shifted
           up to form diagonal branches. Connector lines live in the slot. -->
      <div class="atmos-spirit-tree__columns">
        @for (col of renderColumns(); track col.position) {
          <div class="atmos-spirit-tree__column"
               [class.is-left]="col.position === 'left'"
               [class.is-center]="col.position === 'center'"
               [class.is-right]="col.position === 'right'">
            @if (col.hasBelow) {
              <div class="atmos-spirit-tree__slot">
                @if (col.below; as slot) {
                  <ng-container *ngTemplateOutlet="editSlotTpl; context: { $implicit: slot }"></ng-container>
                }
              </div>
            }
            @for (s of col.slots; track $index) {
              <div class="atmos-spirit-tree__slot">
                @if (s.node; as n) {
                  <app-node
                    [node]="n"
                    [position]="col.position"
                    [subIcons]="nodeSubIcons()"
                    [action]="action()"
                    [opaque]="isNodeOpaque(n)"
                    [highlight]="isNodeHighlighted(n)"
                    [showTooltips]="showNodeTooltips()"
                    [overlayTemplate]="nodeOverlayTemplate()"
                    [drawConnectors]="false"
                    (nodeClicked)="onNodeClicked($event, n)"></app-node>
                  @if (n.n)  { <span class="atmos-conn atmos-conn--up"  aria-hidden="true"></span> }
                  @if (n.nw) { <span class="atmos-conn atmos-conn--nw" aria-hidden="true"></span> }
                  @if (n.ne) { <span class="atmos-conn atmos-conn--ne" aria-hidden="true"></span> }
                }
                @if (s.edit; as slot) {
                  <ng-container *ngTemplateOutlet="editSlotTpl; context: { $implicit: slot }"></ng-container>
                }
              </div>
            }
          </div>
        }
      </div>
    } @else if (tree().tier) {
      <!-- Rows-first grid for tier trees (unchanged). -->
      <div class="atmos-spirit-tree__grid">
        @for (row of rows(); track $index) {
          <div class="atmos-spirit-tree__row">
            <div class="atmos-spirit-tree__cell">
              @if (row.left; as n) {
                <app-node
                  [node]="n"
                  position="left"
                  [subIcons]="nodeSubIcons()"
                  [action]="action()"
                  [opaque]="isNodeOpaque(n)"
                  [highlight]="isNodeHighlighted(n)"
                  [showTooltips]="showNodeTooltips()"
                  [overlayTemplate]="nodeOverlayTemplate()"
                  (nodeClicked)="onNodeClicked($event, n)"></app-node>
              }
            </div>
            <div class="atmos-spirit-tree__cell">
              @if (row.center; as n) {
                <app-node
                  [node]="n"
                  position="center"
                  [subIcons]="nodeSubIcons()"
                  [action]="action()"
                  [opaque]="isNodeOpaque(n)"
                  [highlight]="isNodeHighlighted(n)"
                  [showTooltips]="showNodeTooltips()"
                  [overlayTemplate]="nodeOverlayTemplate()"
                  (nodeClicked)="onNodeClicked($event, n)"></app-node>
              }
            </div>
            <div class="atmos-spirit-tree__cell">
              @if (row.right; as n) {
                <app-node
                  [node]="n"
                  position="right"
                  [subIcons]="nodeSubIcons()"
                  [action]="action()"
                  [opaque]="isNodeOpaque(n)"
                  [highlight]="isNodeHighlighted(n)"
                  [showTooltips]="showNodeTooltips()"
                  [overlayTemplate]="nodeOverlayTemplate()"
                  (nodeClicked)="onNodeClicked($event, n)"></app-node>
              }
            </div>
          </div>
          @if (row.tierStart) {
            <div class="atmos-spirit-tree__tier-separator" aria-hidden="true"></div>
          }
        }
      </div>
    }

    @if (tree().draft) {
      <app-draft-warning />
    }

    <footer class="atmos-spirit-tree__footer">
      <app-cost
        class="atmos-spirit-tree__cost"
        [cost]="totalCost()"
        [remaining]="remainingCost()"
        [borderLeft]="false"></app-cost>
    </footer>
  </div>
</section>

<!-- Editor affordances: a ghost tile on a free direction, a chip on an occupied one. -->
<ng-template #editSlotTpl let-slot>
  @if (slot.kind === 'add') {
    <button type="button" class="atmos-tree-edit atmos-tree-edit--add"
            [skyTooltip]="editSlotTitle(slot)" [attr.aria-label]="editSlotTitle(slot)"
            (click)="onEditSlotClicked(slot, $event)">
      <mat-icon>add</mat-icon>
    </button>
  } @else {
    <button type="button" class="atmos-tree-edit atmos-tree-edit--chip"
            [class.is-unlink]="slot.kind === 'unlink'"
            [attr.data-direction]="slot.direction"
            [skyTooltip]="editSlotTitle(slot)" [attr.aria-label]="editSlotTitle(slot)"
            (click)="onEditSlotClicked(slot, $event)">
      <mat-icon>{{ slot.kind === 'unlink' ? 'link_off' : 'link' }}</mat-icon>
    </button>
  }
</ng-template>
